/**
 * BenchmarkCard - the gold-standard commodity/benchmark card, extracted from
 * the public Commodities page anatomy: icon tile, name + mono "SYM · EXCHANGE"
 * line, DeltaChip, mono price + unit baseline row, 40px sparkline, italic
 * "Updated ·" stamp. Selection is an accentBg wash + 1.5px accent border with
 * no layout shift; keyboard operable with a visible focus ring. The `pending`
 * state stays honest: muted dash, dashed baseline, no fabricated $0.00.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useThemeMode, MONO, numericSx } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';
import DeltaChip from '../public/DeltaChip';
import Sparkline from './Sparkline';
import UpdatedStamp from './UpdatedStamp';

function BenchmarkCard({
  name,
  symbol,
  exchange,
  icon = 'ph:globe-simple',
  price,            // formatted string WITHOUT unit, e.g. "$5,776.00"
  unit,             // e.g. "USD/MT"
  delta,            // number (percent) or null
  sparkData,        // [{ close }] slice, >= 2 points to draw
  sparkColor,
  updatedAt,        // ISO date or null
  stampText,        // overrides the formatted date, e.g. a month for monthly data
  pendingText = 'Awaiting exchange feed',
  selected = false,
  pending = false,  // no feed data yet - render the honest awaiting state
  onSelect,
  minWidth = { xs: 210, sm: 232 },
  grow = false,     // share the row rather than hold a fixed width
  hideIcon = false, // when every card in the row has the same one
}) {
  const { palette } = useThemeMode();
  const { cardSx, iconTileSx, focusRingSx } = useRecipes();

  const activate = (e) => {
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    if (e.type === 'keydown') e.preventDefault();
    if (onSelect) onSelect();
  };

  return (
    // Fixed-width cards overflow a full row and the last one bleeds off the
    // edge, which reads as broken rather than scrollable. `grow` lets a known
    // number of them share the row from lg up, keeping the scroller for
    // narrow screens where it is the right behaviour.
    <Box sx={{
      minWidth: grow ? { xs: 210, sm: 232, lg: 0 } : minWidth,
      flex: grow ? { lg: '1 1 0' } : undefined,
      flexShrink: 0,
      scrollSnapAlign: 'start',
    }}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Load ${name} price chart`}
        aria-pressed={selected}
        onClick={activate}
        onKeyDown={activate}
        sx={{
          ...cardSx,
          cursor: 'pointer',
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: selected ? `1.5px solid ${palette.accent}` : `1px solid ${palette.cardBorder}`,
          bgcolor: selected ? palette.accentBg : palette.card,
          transition: 'border-color .2s, box-shadow .2s',
          '&:hover': selected ? {} : { borderColor: palette.textMuted, boxShadow: palette.shadowMd },
          '&:focus-visible': focusRingSx,
        }}
      >
        {/* Header: icon tile + name/symbol + delta */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
            {/* Dropped when every card in the row carries the same icon:
                it says nothing and costs 40px of a 200px card, which is the
                difference between "Groundnuts" and "Groundn...". */}
            {!hideIcon && (
              <Box sx={iconTileSx}>
                <Icon icon={icon} width={15} height={15} color={palette.navy} />
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.86rem', color: palette.text, lineHeight: 1.2 }}>
                {name}
              </Typography>
              <Typography noWrap sx={{ fontFamily: MONO, fontSize: '0.66rem', color: palette.textMuted }}>
                {symbol}{exchange ? ` · ${exchange}` : ''}
              </Typography>
            </Box>
          </Box>
          {pending || delta == null ? (
            <Box sx={{ px: 0.9, py: 0.3, borderRadius: 999, bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}`, flexShrink: 0 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.58rem', fontWeight: 700, color: palette.textMuted, letterSpacing: '0.06em' }}>
                SOON
              </Typography>
            </Box>
          ) : (
            <DeltaChip value={delta} decimals={2} size="sm" />
          )}
        </Box>

        {/* Price + unit */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
          <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1.3rem', color: pending ? palette.textMuted : palette.text }}>
            {pending ? '-' : price}
          </Typography>
          {unit && (
            <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', color: palette.textMuted }}>{unit}</Typography>
          )}
        </Box>

        {/* Sparkline (dashed baseline while pending) */}
        <Box sx={{ mt: 1, height: 40, display: 'flex', alignItems: 'center' }}>
          {pending || !sparkData || sparkData.length < 2 ? (
            <Box sx={{ width: '100%', borderTop: `1px dashed ${palette.cardBorder}` }} />
          ) : (
            <Sparkline data={sparkData} color={sparkColor || palette.accent} id={`bench-${symbol || name}`} />
          )}
        </Box>

        {/* Stamp */}
        <Box sx={{ mt: 'auto', pt: 1 }}>
          {pending ? (
            <Typography sx={{ fontSize: '0.62rem', color: palette.textMuted, fontStyle: 'italic', letterSpacing: '0.02em' }}>
              {pendingText}
            </Typography>
          ) : stampText ? (
            // Monthly data is dated by its month. "1 Aug 2026" would invite
            // reading a monthly average as a single day's observation.
            <Typography sx={{ fontSize: '0.62rem', color: palette.textMuted, fontStyle: 'italic', letterSpacing: '0.02em' }}>
              {stampText}
            </Typography>
          ) : (
            <UpdatedStamp date={updatedAt} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default BenchmarkCard;
